import React, { useEffect, useState } from "react";
import { Grid, Typography, Box, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../../../common/Header";
import LeftSideNavigation from "../../../../common/LeftSideNavigation";
import Footer from "../../../../common/Footer";
import image1 from "../../../../assets/people/peoplebannerimg.png";
import { config } from "../../../../config/config";
import { apiList, invokeApi } from "../../../../services/apiServices";
import { useCookies } from "react-cookie";
import { useMediaQuery, Button } from "@mui/material";

const PeopleCategoryPage = () => {
  const navigate = useNavigate();
  const { ogUrl } = useParams(); // Get ogUrl from URL params
  const [cookies] = useCookies();

  const [peopleData, setPeopleData] = useState([]);
  const [filteredPeople, setFilteredPeople] = useState([]);
  const [categoryTitle, setCategoryTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isMobile = useMediaQuery("(max-width:600px)");

  // ============ Fetch Dynamic People Data ============
  useEffect(() => {
    const fetchPeople = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await invokeApi(
          config.getMyCollege + apiList.getPeople,
          null,
          cookies
        );

        console.log("People API Response:", response);

        if (response?.status === 200) {
          const responseData = response.data;
          let allPeople = [];

          if (Array.isArray(responseData)) {
            allPeople = responseData;
          } else if (
            responseData?.people &&
            Array.isArray(responseData.people)
          ) {
            allPeople = responseData.people;
          } else if (responseData?.data && Array.isArray(responseData.data)) {
            allPeople = responseData.data;
          } else if (responseData?.items && Array.isArray(responseData.items)) {
            allPeople = responseData.items;
          }

          console.log("Extracted people:", allPeople);

          if (allPeople.length > 0) {
            setPeopleData(allPeople);

            // Filter people based on ogUrl from URL
            if (ogUrl) {
              // Find the category that matches the ogUrl
              const categories = [...new Set(allPeople.map(person => person.category).filter(Boolean))];
              const matchedCategory = categories.find(cat =>
                cat.toLowerCase().replace(/\s+/g, '-') === ogUrl.toLowerCase()
              );

              if (matchedCategory) {
                const filtered = allPeople.filter(
                  (person) => person.category === matchedCategory
                );
                setFilteredPeople(filtered);
                setCategoryTitle(matchedCategory);
                console.log(`Filtered ${matchedCategory} people:`, filtered);
              } else {
                // If no category found by name, try to find by ogUrl field
                const filteredByOgUrl = allPeople.filter(
                  (person) => person.ogUrl === ogUrl || person.categoryUrl === ogUrl
                );

                if (filteredByOgUrl.length > 0) {
                  // Get the category from the first person found
                  const foundCategory = filteredByOgUrl[0].category;
                  const filtered = allPeople.filter(
                    (person) => person.category === foundCategory
                  );
                  setFilteredPeople(filtered);
                  setCategoryTitle(foundCategory);
                } else {
                  setFilteredPeople([]);
                  setCategoryTitle("Category Not Found");
                }
              }
            } else {
              // If no ogUrl specified, show all people
              setFilteredPeople(allPeople);
              setCategoryTitle("All People");
            }
          } else {
            setError("No people found");
            setFilteredPeople([]);
            setCategoryTitle("No Data");
          }
        } else {
          setError(`Failed to fetch people: ${response?.status}`);
          console.error("Failed to fetch people response:", response);
          setFilteredPeople([]);
          setCategoryTitle("Error");
        }
      } catch (error) {
        console.error("People API error:", error);
        setError("Failed to load people data");
        setFilteredPeople([]);
        setCategoryTitle("Error");
      } finally {
        setLoading(false);
      }
    };

    fetchPeople();
    window.scroll(0, 0);
  }, [cookies, ogUrl]); // Add ogUrl to dependency array

  if (loading) {
    return (
      <>
        <Header />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error && filteredPeople.length === 0) {
    return (
      <>
        <Header />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            flexDirection: "column",
            p: 3,
          }}
        >
          <Typography color="error" variant="h6" gutterBottom>
            {error}
          </Typography>
          <Typography variant="body1">
            Please check your connection and try again.
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <Grid container>
        <Header />
        {/* Full-width Hero Image */}
        {/* <Grid
          item
          xs={12}
          sx={{
            height: { xs: "60vh", md: "70vh" },
            backgroundImage: `url(${image1})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        /> */}

        {/* Main Content Area - Below Hero Image */}
        <Grid container sx={{ bgcolor: "#f5f5f5", color: "#fff", mt: isMobile ? 12 : 10 }}>
          {/* For desktop - Left Side Navigation appears first (left side) */}
          <Grid
            item
            md={3}
            sx={{
              p: 3,
              display: { xs: "none", md: "block" },
              mt: 11,
            }}
          >
            <LeftSideNavigation />
          </Grid>

          {/* Right Side Content - Takes full width on mobile, 75% on desktop */}
          <Grid
            item
            xs={12}
            md={8}
            sx={{
              p: 3,
              boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
              borderRadius: "8px",
              margin: { md: "24px 24px 24px 0" },
              backgroundColor: "#fff",
            }}
          >
            <Grid
              item
              xs={12}
              sx={{
                px: 3,
                py: 4,
                justifyContent: "center",
              }}
            >
              <Typography
                variant="h5"
                align="center"
                gutterBottom
                sx={{
                  fontWeight: "bold",
                  mb: 3,
                  fontFamily: "Montserrat, sans-serif",
                  color: "#000",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                }}
              >
                {categoryTitle}
              </Typography>

              {filteredPeople.length > 0 ? (
                <Grid container spacing={3} justifyContent="center">
                  {filteredPeople.map((member, memIndex) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={member.id || memIndex}
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          width: 280,
                          height: 380,
                          borderRadius: 4,
                          position: "relative",
                          overflow: "hidden",
                          boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
                          cursor: "default", // Default cursor since image isn't clickable
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: "0 12px 30px rgba(0,0,0,0.2)",
                            "& .view-profile-btn": {
                              opacity: 1,
                              transform: "translateY(0)",
                            },
                            "& .member-image": {
                              transform: "scale(1.1)",
                            },
                            "& .overlay-content": {
                              paddingBottom: 4,
                            },
                          },
                        }}
                      >
                        {/* Image Container */}
                        <Box
                          sx={{
                            width: "100%",
                            height: "100%",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            className="member-image"
                            src={
                              member.imageUrl ||
                              member.featuredUrl ||
                              "/default-profile.jpg"
                            }
                            alt={member.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              transition: "transform 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                            }}
                            onError={(e) => {
                              e.target.src = "/default-profile.jpg";
                            }}
                          />
                        </Box>

                        {/* Gradient Overlay & Content */}
                        <Box
                          className="overlay-content"
                          sx={{
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            width: "100%",
                            height: "60%", // Covers bottom 60%
                            background:
                              "linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 70%, transparent 100%)",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-end",
                            alignItems: "center",
                            pb: 2,
                            transition: "all 0.4s ease",
                          }}
                        >
                          <Typography
                            variant="h6"
                            align="center"
                            sx={{
                              color: "#fff",
                              fontWeight: "bold",
                              fontFamily: "Montserrat, sans-serif",
                              fontSize: "0.8rem",
                              mb: 0.5,
                              textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                            }}
                          >
                            {member.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            align="center"
                            sx={{
                              color: "rgba(255, 255, 255, 0.9)",
                              fontFamily: "Montserrat, sans-serif",
                              fontSize: "0.65rem",
                              mb: 1,
                              fontWeight: 500,
                              letterSpacing: "0.5px",
                            }}
                          >
                            {member.position}
                          </Typography>

                          <Button
                            className="view-profile-btn"
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/profile/${member.id || memIndex}`, {
                                state: { member },
                              });
                            }}
                            sx={{
                              opacity: 0,
                              transform: "translateY(20px)",
                              transition:
                                "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                              color: "#fff",
                              borderColor: "#fff",
                              borderRadius: "20px",
                              mt: 1,
                              px: 3,
                              py: 0.5,
                              fontSize: "0.75rem",
                              textTransform: "uppercase",
                              letterSpacing: "1px",
                              "&:hover": {
                                borderColor: "#fff",
                                backgroundColor: "rgba(255,255,255,0.2)",
                              },
                            }}
                          >
                            View Profile
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "200px",
                  }}
                >
                  <Typography variant="h6" color="textSecondary">
                    No people found in {categoryTitle}
                  </Typography>
                </Box>
              )}
            </Grid>
          </Grid>

          {/* For mobile - Left Side Navigation appears after main content */}
          <Grid
            item
            xs={12}
            sx={{
              p: 3,
              display: { xs: "block", md: "none" },
            }}
          >
            <LeftSideNavigation />
          </Grid>
        </Grid>
      </Grid>
      <Footer variant="light" />
    </>
  );
};

export default PeopleCategoryPage;